/**
 * Duration parsing — THE single implementation, shared by both runtimes.
 *
 * Written as CommonJS so the frontend can `import` it (webpack interop) and
 * the backend can `require` it, from one file with no mirror to keep in sync.
 * Same pattern as `src/config/termsVersion.js`; it lives under `src/` because
 * CRA's ModuleScopePlugin refuses imports from outside that tree, so `src/` is
 * the only viable home for genuinely shared code in this project.
 *
 * History worth keeping: this logic existed three times (here, in
 * `tripsService.saveActivitiesToTrip`, and in the POST /trips/:id/activities
 * route). They disagreed — the route's took the first number in the string, so
 * "3 days" stored as 3 MINUTES, and the frontend copy read Tiqets "2:30" as
 * 2 hours. Consolidating removed the drift rather than testing for it.
 *
 * Frontend callers import `parseDurationToMinutes` from `./dateUtils`, which
 * re-exports it — that keeps the historical import path working.
 */

// A "day" of touring is 8 hours, not 24 — this is a SCHEDULING answer ("how
// much of the day does this consume"). Spanning is a different question; see
// spanDaysFromDuration.
const MINUTES_PER_DAY_ACTIVITY = 480;

/**
 * Parse a duration of any stored shape into minutes. Handles numbers (already
 * minutes), stringified minutes ("90"), "HH:MM" (Tiqets), "2h 30m",
 * "2.5 hours", "90 minutes", "2-3 hours" (lower bound), "1 day" (= 8h), and
 * vague labels ("half day", "full day", "morning", "evening").
 *
 * @param {number|string|null|undefined} duration
 * @param {number} [fallback=60] returned when missing/unparseable
 * @returns {number} minutes
 */
function parseDurationToMinutes(duration, fallback = 60) {
  if (duration === null || duration === undefined || duration === '') return fallback;
  if (typeof duration === 'number') {
    // Guard nonsense (NaN, negatives) and keep minutes whole.
    return Number.isFinite(duration) && duration > 0 ? Math.round(duration) : fallback;
  }
  const str = String(duration).toLowerCase().trim();
  if (!str) return fallback;

  // "HH:MM" (Tiqets product duration) — must precede the numeric branches, or
  // "2:30" falls through to the loose-number match and reads as 2 HOURS.
  const clock = str.match(/^(\d{1,3}):([0-5]\d)$/);
  if (clock) return parseInt(clock[1], 10) * 60 + parseInt(clock[2], 10);

  // Stringified number = minutes (matches how numeric durations serialize)
  if (/^\d+$/.test(str)) return parseInt(str, 10);

  if (str.includes('half day') || str.includes('half-day')) return 240;
  if (str.includes('full day') || str.includes('full-day')) return MINUTES_PER_DAY_ACTIVITY;
  if (str.includes('morning') || str.includes('afternoon')) return 240;
  if (str.includes('evening')) return 180;

  const dayMatch = str.match(/(\d+\.?\d*)\s*days?\b/);
  if (dayMatch) return Math.round(parseFloat(dayMatch[1]) * MINUTES_PER_DAY_ACTIVITY);

  // "2h 30m" / "2 hours 30 minutes"
  const hourMin = str.match(/(\d+)\s*h(?:ours?|rs?)?\s*(\d+)\s*m(?:in(?:ute)?s?)?/);
  if (hourMin) return parseInt(hourMin[1], 10) * 60 + parseInt(hourMin[2], 10);

  // "2-3 hours" — take the lower bound
  const range = str.match(/(\d+\.?\d*)\s*-\s*\d+\.?\d*\s*h(?:ours?|rs?)?/);
  if (range) return Math.round(parseFloat(range[1]) * 60);

  const hours = str.match(/(\d+\.?\d*)\s*h(?:ours?|rs?)?\b/);
  if (hours) return Math.round(parseFloat(hours[1]) * 60);

  const mins = str.match(/(\d+\.?\d*)\s*m(?:in(?:ute)?s?)?\b/);
  if (mins) return Math.round(parseFloat(mins[1]));

  // Last resort: a number buried in prose ("about 2") reads as hours
  const loose = str.match(/\d+\.?\d*/);
  if (loose) return Math.round(parseFloat(loose[0]) * 60);

  return fallback;
}

/**
 * Whole days an activity of `minutes` covers, for deriving a day-span
 * `end_date`. Uses a REAL day (24h), not the 8h touring day: a span is only
 * claimed once the activity genuinely runs past a single day, so a "full day"
 * 8h tour stays single-day while a 3-day trek spans.
 *
 * @param {number} minutes
 * @returns {number} 1 for same-day, 2+ for multi-day
 */
function spanDaysFromMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return 1;
  const REAL_DAY = 24 * 60;
  return minutes <= REAL_DAY ? 1 : Math.ceil(minutes / REAL_DAY);
}

/**
 * Calendar days an activity covers, from a duration of ANY shape.
 *
 * Text and numbers must not disagree here. `parseDurationToMinutes` maps
 * "3 days" to 3 x 8h = 1440 (the scheduling answer), which as raw minutes
 * would read as a single 24h day. Spanning is a different question, so an
 * explicit "N days" phrase is honored as N calendar days directly; everything
 * else falls through to elapsed minutes (Viator publishes real elapsed time —
 * 4320 for a 3-day tour).
 *
 * @param {number|string|null} duration
 * @returns {number} 1 for same-day, 2+ for multi-day
 */
function spanDaysFromDuration(duration) {
  if (typeof duration === 'string') {
    const days = duration.toLowerCase().match(/(\d+(?:\.\d+)?)\s*days?\b/);
    if (days) {
      const n = Math.round(parseFloat(days[1]));
      return Number.isFinite(n) && n > 1 ? n : 1;
    }
  }
  return spanDaysFromMinutes(parseDurationToMinutes(duration, 0));
}

module.exports = {
  parseDurationToMinutes,
  spanDaysFromMinutes,
  spanDaysFromDuration,
  MINUTES_PER_DAY_ACTIVITY,
};
